/**
 * Development Authentication Codes
 *
 * Instant role sign-in on the local Vite / LAN dev server. Each code maps to a
 * synthetic `<role>@dev.local` Firebase Auth account. The shared password is
 * read only on that server (see `devAuthPassword.js`); production builds never
 * expose it.
 *
 * SECURITY: {@link isDevAuthEnvironment} is false in every production build and
 * on any non-local hostname. Deployed hosting cannot use these codes.
 */

/** Shared facility id for the facility role code. */
export const DEV_FACILITY_ID = "dev-clinic-001";

/** Institution id for the Fusion Affiliate dev code. CareMap Ads resolves bids through it. */
export const DEV_FUSION_AFFILIATE_ID = "dev-fusion-affiliate";

/** Fixed ChartMind Admin org for code 400001. */
export const DEV_CHARTMIND_ORG_ID = "dev-chartmind-org";

/** Institution-scoping keys users cannot self-write (firestore.rules update lock). */
export const DEV_AFFILIATION_FIELD_KEYS = [
  "activeInstitutionId",
  "institutionId",
  "organizationId",
  "region",
  "hierarchyInstitutionId",
];

/**
 * Drop affiliation fields from a client merge of an existing user doc.
 * @param {object|null|undefined} fields
 * @returns {object}
 */
export function omitDevAffiliationFields(fields) {
  if (!fields || typeof fields !== "object") return {};
  const next = { ...fields };
  DEV_AFFILIATION_FIELD_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

/**
 * True when numeric dev auth codes may be used.
 * Local Vite / LAN only — never production builds or deployed hosts.
 */
export const isDevAuthEnvironment = () => {
  if (process.env.NODE_ENV === "production") return false;
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1" ||
    hostname.startsWith("192.168.")
  );
};

/**
 * One code per dashboard role from docs/roles-and-applications.md.
 */
export const DEV_AUTH_CODES = {
  "100001": {
    role: "patient",
    name: "Test Patient",
    email: "patient@dev.local",
    description: "Patient — CareStart, MasterChart, medications, CareMap on sandbox.",
  },
  "200001": {
    role: "professional",
    name: "Test Professional",
    email: "professional@dev.local",
    description: "Healthcare Provider — ChartMind, PeerView, My Charts, CareMap.",
  },
  "500001": {
    role: "facility",
    name: "Test Facility",
    email: "facility@dev.local",
    description: "Healthcare Organization — VisitStart / Check-ins, CareMap.",
    facilityId: DEV_FACILITY_ID,
  },
  "400001": {
    role: "admin",
    name: "ChartMind Admin",
    email: "chartmind-admin@dev.local",
    description: "ChartMind Admin — QueryManager, Team, Usage, CareMap.",
    organizationId: DEV_CHARTMIND_ORG_ID,
  },
  "700001": {
    role: "learner",
    name: "Test Learner",
    email: "learner@dev.local",
    description: "Learner — MorningReport only.",
  },
  "130015": {
    role: "regional_admin",
    name: "Regional Admin - East Africa",
    email: "regional-admin-eastafrica@dev.local",
    description: "Regional admin — QueryManager for region east_africa.",
    region: "east_africa",
    scope: { regions: ["east_africa"], localities: [] },
  },
  "130001": {
    role: "admin",
    name: "Platform Global Admin",
    email: "eco-global-admin@dev.local",
    description:
      "Global admin — Auth claim admin:true via stampDevAuthProfile (code 130001).",
  },
  // Same code as FusionMSI. Application: BTYB only.
  "900002": {
    role: "btybMarketer",
    name: "BTYB Marketer",
    email: "btyb-marketer@dev.local",
    description: "BTYB Marketer — city CPM auction for the Brought to you by banner.",
  },
  // Fusion Affiliate is the carousel-ads advertiser. Application: CareMap Ads only.
  "900003": {
    role: "fusionAffiliate",
    name: "Fusion Affiliate",
    email: "fusion-affiliate@dev.local",
    description: "Fusion Affiliate — CareMap Ads (carousel / zip-slot ads) only.",
    organizationId: DEV_FUSION_AFFILIATE_ID,
    facilityId: DEV_FUSION_AFFILIATE_ID,
  },
};

/**
 * @param {string} code
 * @returns {boolean}
 */
export const isDevAuthCode = (code) => {
  if (!isDevAuthEnvironment()) return false;
  return Object.prototype.hasOwnProperty.call(DEV_AUTH_CODES, code);
};

/**
 * @param {string} code
 * @returns {object|null}
 */
export const getDevAuthData = (code) => {
  if (!isDevAuthCode(code)) return null;
  return DEV_AUTH_CODES[code];
};

/**
 * Affiliation fields written to `users/{uid}` on dev login.
 * @param {object|null} devData
 * @returns {object}
 */
export const buildDevUserAffiliationFields = (devData) => {
  if (!devData) return {};
  const fields = {};
  if (devData.facilityId) {
    fields.facilityId = devData.facilityId;
    fields.clinicId = devData.facilityId;
  }
  if (devData.organizationId) {
    fields.organizationId = devData.organizationId;
    fields.hierarchyInstitutionId = devData.organizationId;
    fields.activeInstitutionId = devData.organizationId;
  }
  if (devData.region) {
    fields.region = devData.region;
  }
  if (devData.scope) {
    fields.scope = devData.scope;
  }
  return fields;
};

/**
 * Profile fields stamped on `users/{uid}`.
 * @param {object|null} devData
 * @returns {object}
 */
export const buildDevUserDocFields = (devData) => {
  if (!devData) return {};
  const fields = {
    name: devData.name,
    isDevAccount: true,
    onboarding: false,
    ...buildDevUserAffiliationFields(devData),
  };
  if (devData.role != null && devData.role !== "") {
    fields.role = devData.role;
  }
  return fields;
};

/**
 * Facility dashboards resolve via `locations` membership, not `users.facilityId`
 * alone. Idempotently create/merge the location and array-union the uid.
 *
 * @param {FirebaseFirestore.Firestore} db
 * @param {typeof import("firebase/compat/app").default} firebase
 * @param {{ uid: string, facilityId: string, title?: string }} opts
 */
export const ensureDevFacilityLocation = async (
  db,
  firebase,
  { uid, facilityId, title },
) => {
  const id = String(facilityId || "").trim();
  if (!uid || !id) return null;

  const ref = db.collection("locations").doc(id);
  const snap = await ref.get();

  if (!snap.exists) {
    await ref.set({
      id,
      title: title || "Dev Facility",
      type: "Clinic",
      status: "approved",
      users: [uid],
      waitTimes: [],
      diagnosticCapabilities: [],
      capabilities: {
        lab: false,
        xray: false,
        ultrasound: false,
        ct: false,
        mri: false,
      },
    });
  } else {
    await ref.set(
      {
        users: firebase.firestore.FieldValue.arrayUnion(uid),
        ...(snap.data()?.title ? {} : { title: title || "Dev Facility" }),
      },
      { merge: true },
    );
  }

  return id;
};

/**
 * @returns {{ code: string, role: string, name: string, email: string, description: string }[]}
 */
export const getAllDevCodes = () => {
  if (!isDevAuthEnvironment()) return [];
  return Object.entries(DEV_AUTH_CODES).map(([code, data]) => ({
    code,
    ...data,
  }));
};

export default DEV_AUTH_CODES;
